import { useEffect, useState, useRef } from "react";
import { X } from "lucide-react";
import Tab from "Common/Components/Tab/Tab";
import TableContainer from "Common/TableContainer";
import { Dropdown } from 'Common/Components/Dropdown';
import Modal from "Common/Components/Modal";
import { MoreHorizontal, FileEdit } from 'lucide-react';
import { getLoanApplicationList } from "../../helpers/fakebackend_helper";
import LoanApplicationModalContent from "./modals/LoanApplicationModalContent";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useLoader } from "../../context/LoaderContext";
import CurrencyGTCell from "Common/Components/CurrencyGTCell";


const LoanApplicationList = () => {
    const [customers, setCustomers] = useState([]);
    const loanApplicationFormRef = useRef<any>(null);
    const patrimonialStateFormRef = useRef<any>(null);
    const warrantyFormRef = useRef<any>(null);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const completedFormRef = useRef<any>(null);
    const [activeTab, setActiveTab] = useState<string>("loanApplicationForm");
    const [openModal, setOpenModal] = useState<boolean>(false);
    const { showLoader, hideLoader } = useLoader();
    const tabOrder = [
        "loanApplicationForm",
        "patrimonialStateForm",
        "warrantyForm",
        "completed"
    ];

    const resetTabRef = useRef<() => void>();

    const closeToggle = () => {
        setOpenModal(false);
        setIsEdit(false);
        loanApplicationFormRef.current?.resetForm?.();
        patrimonialStateFormRef.current?.resetForm?.();
        warrantyFormRef.current?.resetForm?.();
        completedFormRef.current?.resetForm?.();
        setActiveTab("loanApplicationForm");
        resetTabRef.current?.();
        fetchLoanApplications();
    };

    useEffect(() => {
        fetchLoanApplications();
    }, []);

    const fetchLoanApplications = async () => {
        showLoader();
        try {
            const params = {
                page: 0,
                size: 1000,
                sort: "loanApplicationId,desc"
            };

            const response = await getLoanApplicationList(params);
            const loanApplications = response.data?._embedded?.loanApplications || [];
            setCustomers(loanApplications);
        } catch (error) {
            toast.error("Error fetching loan applications: " + error, { autoClose: 2000 });
        }finally {
            hideLoader();
        }
    };

    const handleEdit = (loanApplication: any) => {
        setActiveTab("loanApplicationForm");
        setOpenModal(true);
        setIsEdit(true);
        loanApplicationFormRef.current?.setFormData?.(loanApplication);
    };

    const handleNewApplication = () => {
        setIsEdit(false); // Modo creación
        setActiveTab("loanApplicationForm");
        setOpenModal(true);
        loanApplicationFormRef.current?.resetForm?.();
    };

    const columns = [
    {
      header: "Numero de Prestamo",
      accessorKey: "loanNumber",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Cliente",
      accessorKey: "customer.names",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Tipo de Prestamo",
      accessorKey: "typeLoan.name",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Saldo solicitado",
      accessorKey: "amountRequested",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ getValue }: any) => <CurrencyGTCell value={getValue()} />,
    },
    {
      header: "Saldo aprobado",
      accessorKey: "approvedAmount",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ getValue }: any) => <CurrencyGTCell value={getValue()} />,
    },
    {
      header: "Estado",
      accessorKey: "loanApplicationStatus.name",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
        header: "Acciones",
        cell: ({ row }: any) => (
          <div className="flex justify-center">
            <Dropdown className="relative">
              <Dropdown.Trigger className="flex items-center justify-center size-8 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-600 hover:text-white focus:outline-none">
                <MoreHorizontal className="size-4" />
              </Dropdown.Trigger>
              <Dropdown.Content className="absolute z-50 right-0 mt-2 w-40 bg-white rounded-md shadow-lg dark:bg-zink-600">
              <ul>
              <li>
                <button
                  type="button"
                  onClick={() => handleEdit(row.original)}
                  className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-zink-100 dark:hover:bg-zink-500"
                >
                  <FileEdit className="inline size-4 mr-2" />
                  Editar
                </button>
              </li>
            </ul>
              </Dropdown.Content>
            </Dropdown>
          </div>
        ),
      }
  ];

    const handleSubmitByTab = () => {
        switch (activeTab) {
            case "loanApplicationForm":
                loanApplicationFormRef.current?.submitForm?.();
                break;
            case "patrimonialStateForm":
                patrimonialStateFormRef.current?.submitForm?.();
                break;
            case "warrantyForm":
                warrantyFormRef.current?.submitForm?.();
                break;
            case "completed":
                completedFormRef.current?.submitForm?.();
                break;
            default:
                toast.error("Error: Tab not found", { autoClose: 2000 });
        }
    };


    return (
        <div className="card">
            <div className="card-body">
                <div className="mb-3">
                    <div className="flex justify-end">
                        <button
                            className="text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100" id="addRow"
                            onClick={handleNewApplication}
                        >
                            Nueva Solicitud
                        </button>
                    </div>
                    <Modal show={openModal} onHide={closeToggle} id="openModal" modal-center="true"
                        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
                        dialogClassName="w-screen max-w-7xl bg-white shadow rounded-md dark:bg-zink-600 flex flex-col h-full">
                        <Modal.Header className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500"
                            closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500 dark:text-zink-200 dark:hover:text-red-500">
                            <Modal.Title className="text-16">Agregar nueva solicitud</Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
                        <div className="grid grid-cols-1 2xl:grid-cols-12">
                    <div className="col-span-12 2xl:col-start-3 2xl:col-span-8">
                            <div>
                                <Tab.Container
                                    defaultActiveKey="loanApplicationForm"
                                    onTabChange={(key) => setActiveTab(key)}
                                >
                                    <LoanApplicationModalContent
                                        activeTab={activeTab}
                                        setActiveTab={setActiveTab}
                                        tabOrder={tabOrder}
                                        loanApplicationFormRef={loanApplicationFormRef}
                                        patrimonialStateFormRef={patrimonialStateFormRef}
                                        warrantyFormRef={warrantyFormRef}
                                        isEdit={isEdit}
                                        completedFormRef={completedFormRef}
                                        onResetTab={(fn) => (resetTabRef.current = fn)}
                                        closeToggle={closeToggle}
                                    />
                                </Tab.Container>

                            </div>
                    </div>
                </div>
                        </Modal.Body>
                        <Modal.Footer className="flex items-center justify-end p-4 mt-auto border-t border-slate-200 dark:border-zink-500">
                            <div className="flex justify-end gap-2 mt-5">
                                <button type="button" onClick={closeToggle} className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-700 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"><X className="inline-block size-4" /> <span className="align-middle">Cancelar</span></button>
                                <button
                                    onClick={handleSubmitByTab}
                                    className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                                >
                                {activeTab === "completed" ? "Finalizar" : "Siguiente"}
                                </button>
                            </div>
                        </Modal.Footer>
                    </Modal>
                </div>
                <TableContainer
                    isPagination={true}
                    isSelect={true}
                    isGlobalFilter={true}
                    columns={columns}
                    data={customers}
                    customPageSize={5}
                    divclassName="my-2 col-span-12 overflow-x-auto lg:col-span-12"
                    tableclassName="hover group dataTable w-full text-sm align-middle whitespace-nowrap no-footer"
                    theadclassName="border-b border-slate-200 dark:border-zink-500"
                    trclassName="group-[.stripe]:even:bg-slate-50 group-[.stripe]:dark:even:bg-zink-600 transition-all duration-150 ease-linear group-[.hover]:hover:bg-slate-50 dark:group-[.hover]:hover:bg-zink-600 [&.selected]:bg-custom-500 dark:[&.selected]:bg-custom-500 [&.selected]:text-custom-50 dark:[&.selected]:text-custom-50"
                    thclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500 sorting px-3 py-4 text-slate-900 bg-slate-200/50 font-semibold text-left dark:text-zink-50 dark:bg-zink-600 dark:group-[.bordered]:border-zink-500"
                    tdclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500"
                    PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
                />
            </div>

        </div>
    );
};

export default LoanApplicationList;
